import { SOCIAL_PLATFORMS, SOCIAL_PLATFORM_LABELS, type SocialPlatform } from '@nova/shared';
import { useState } from 'react';
import { useFilterParams } from '../app/FilterProvider.js';
import { useAuth } from '../auth/AuthProvider.js';
import { cn } from '../lib/cn.js';
import { SocialOverview } from '../features/social/SocialOverview.js';
import { SocialRegister } from '../features/social/SocialRegister.js';
import { useSocialAccounts, useSocialOverview } from '../features/social/useSocial.js';

type Tab = 'overview' | 'register';

export function SoMePage() {
  const { company } = useFilterParams();
  const { user } = useAuth();
  const canEdit = user?.role !== 'VIEWER';

  const [tab, setTab] = useState<Tab>('overview');
  const [platform, setPlatform] = useState<SocialPlatform | 'ALLE'>('ALLE');

  const filter = { company, platform: platform === 'ALLE' ? undefined : platform };

  const overview = useSocialOverview(filter);
  const accounts = useSocialAccounts(filter);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-display text-ink text-xl">SoMe</h1>
        <p className="text-ink-soft mt-0.5 text-sm">
          Oversikt over selskapenes sosiale kontoer og tallene deres over tid. Tallene legges inn
          manuelt én gang i måneden.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* View switch */}
        <div className="border-hairline bg-raised/40 inline-flex rounded-lg border p-0.5">
          <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>
            Overvåkning
          </TabButton>
          <TabButton active={tab === 'register'} onClick={() => setTab('register')}>
            Register
          </TabButton>
        </div>

        {/* Platform filter */}
        <div className="flex items-center gap-1.5">
          <Chip active={platform === 'ALLE'} onClick={() => setPlatform('ALLE')}>
            Alle plattformer
          </Chip>
          {SOCIAL_PLATFORMS.map((p) => (
            <Chip key={p} active={platform === p} onClick={() => setPlatform(p)}>
              {SOCIAL_PLATFORM_LABELS[p]}
            </Chip>
          ))}
        </div>
      </div>

      {tab === 'overview' ? (
        <SocialOverview accounts={overview.data ?? []} isLoading={overview.isLoading} />
      ) : (
        <SocialRegister
          accounts={accounts.data ?? []}
          isLoading={accounts.isLoading}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm transition',
        active ? 'bg-accent text-base font-semibold' : 'text-ink-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-[11px] transition',
        active
          ? 'border-accent/50 bg-accent/[0.08] text-ink'
          : 'border-hairline text-ink-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
